import React, { useMemo, useState } from 'react';
import { analyzeFBA, REFERRAL_RATES, FEE_SCHEDULE_VERSION } from '../lib/fba';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'rgba(10, 10, 15, 0.6)',
  border: '1px solid rgba(102, 126, 234, 0.3)',
  borderRadius: '8px',
  color: 'white',
  fontSize: '0.9rem',
};

const labelStyle = {
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: '0.75rem',
  display: 'block',
  marginBottom: '4px',
};

const Field = ({ label, children }) => (
  <div>
    <label style={labelStyle}>
      {label}
      {children}
    </label>
  </div>
);

const Num = ({ value, onChange, ...rest }) => (
  <input
    type="number"
    inputMode="decimal"
    min="0"
    step="any"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={inputStyle}
    {...rest}
  />
);

const Row = ({ name, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{name}</span>
    <span style={{ color: strong || 'white', fontSize: '0.85rem', fontWeight: strong ? 700 : 400 }}>{value}</span>
  </div>
);

/**
 * FBA profitability calculator. Pass `salePrice` to prefill from a product.
 */
export default function FBACalculator({ salePrice: initialPrice }) {
  const [salePrice, setSalePrice] = useState(initialPrice ?? '');
  const [cost, setCost] = useState('');
  const [inbound, setInbound] = useState('0');
  const [category, setCategory] = useState('default');
  const [weightLb, setWeightLb] = useState('1');
  const [lengthIn, setLengthIn] = useState('10');
  const [widthIn, setWidthIn] = useState('7');
  const [heightIn, setHeightIn] = useState('2');

  // Adopt a new prefill when the prop changes (React "derive state from props" pattern).
  const [lastInitialPrice, setLastInitialPrice] = useState(initialPrice);
  if (initialPrice !== lastInitialPrice) {
    setLastInitialPrice(initialPrice);
    if (initialPrice != null) setSalePrice(String(initialPrice));
  }

  const result = useMemo(() => {
    const p = parseFloat(salePrice);
    const c = parseFloat(cost);
    if (!(p > 0) || !(c >= 0) || Number.isNaN(c)) return null;
    return analyzeFBA({
      salePrice: p,
      cost: c,
      inboundShipping: parseFloat(inbound) || 0,
      category,
      weightLb: parseFloat(weightLb),
      lengthIn: parseFloat(lengthIn),
      widthIn: parseFloat(widthIn),
      heightIn: parseFloat(heightIn),
    });
  }, [salePrice, cost, inbound, category, weightLb, lengthIn, widthIn, heightIn]);

  const verdict = result?.supported
    ? result.roiPct >= 30 ? { text: 'GOOD BUY', color: '#22c55e' }
      : result.roiPct > 0 ? { text: 'THIN MARGIN', color: '#eab308' }
      : { text: 'LOSS', color: '#ef4444' }
    : null;

  return (
    <div
      role="region"
      aria-label="FBA profit calculator"
      style={{
        background: 'rgba(10, 10, 15, 0.6)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        borderRadius: '20px',
        padding: '20px',
      }}
    >
      <h3 style={{ color: 'white', marginBottom: '4px', fontSize: '1.05rem' }}>FBA Profit Calculator</h3>
      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', marginBottom: '14px' }}>
        US fee schedule {FEE_SCHEDULE_VERSION} · incl. 3.5% fuel surcharge · verify against Seller Central
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginBottom: '10px' }}>
        <Field label="Sale price ($)"><Num value={salePrice} onChange={setSalePrice} aria-label="Sale price" /></Field>
        <Field label="Your cost ($)"><Num value={cost} onChange={setCost} aria-label="Your cost" /></Field>
        <Field label="Inbound ship ($)"><Num value={inbound} onChange={setInbound} aria-label="Inbound shipping" /></Field>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle} aria-label="Category">
            {Object.keys(REFERRAL_RATES).map((k) => (
              <option key={k} value={k}>{k === 'default' ? 'most categories (15%)' : k.replace('_', ' ')}</option>
            ))}
          </select>
        </Field>
        <Field label="Weight (lb)"><Num value={weightLb} onChange={setWeightLb} aria-label="Weight in pounds" /></Field>
        <Field label="L (in)"><Num value={lengthIn} onChange={setLengthIn} aria-label="Length inches" /></Field>
        <Field label="W (in)"><Num value={widthIn} onChange={setWidthIn} aria-label="Width inches" /></Field>
        <Field label="H (in)"><Num value={heightIn} onChange={setHeightIn} aria-label="Height inches" /></Field>
      </div>

      {result && !result.supported && (
        <p style={{ color: '#eab308', fontSize: '0.85rem' }} role="alert">
          Oversize item — this calculator covers standard-size tiers only.
        </p>
      )}

      {result?.supported && (
        <div style={{ borderTop: '1px solid rgba(102,126,234,0.2)', paddingTop: '10px' }}>
          <Row name={`Referral fee`} value={`-$${result.referralFee.toFixed(2)}`} />
          <Row name={`FBA fulfillment (${result.sizeTier.replace('_', ' ')}, ${result.shippingWeightLb} lb)`} value={`-$${result.fulfillmentFee.toFixed(2)}`} />
          <Row name="Storage (1 mo)" value={`-$${result.storageFee.toFixed(2)}`} />
          <Row name="Net profit / unit" value={`$${result.netProfit.toFixed(2)}`} strong={result.netProfit >= 0 ? '#22c55e' : '#ef4444'} />
          <Row name="Margin" value={`${result.marginPct.toFixed(1)}%`} />
          <Row name="ROI" value={result.roiPct == null ? '—' : `${result.roiPct.toFixed(1)}%`} />
          <Row name="Breakeven price" value={`$${result.breakevenPrice.toFixed(2)}`} />
          {verdict && (
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <span style={{
                color: verdict.color,
                border: `1px solid ${verdict.color}`,
                borderRadius: '999px',
                padding: '4px 14px',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
              }}>
                {verdict.text}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
